// apps/web/src/data/howToTutorials.ts
/**
 * Tutorial modules data for the K&C Logistics "How To" guide.
 * Includes both Light and Dark mode Cloudinary video links for every workflow.
 */

export interface TutorialVideoSource {
  url?: string;
  poster?: string;
  publicId?: string; // Cloudinary public_id
}

export interface TutorialStep {
  title: string;
  description: string;
  timestamp?: string; // e.g., "0:15"
}

export interface TutorialModule {
  id: string;
  title: string;
  category: 'client' | 'transporter' | 'fleet-manager' | 'fleet-driver' | 'emergency';
  categoryLabel: string;
  audienceBadge: string;
  badgeColor: string;
  description: string;
  duration: string;
  iconName: string;
  steps: TutorialStep[];
  videos: {
    light: TutorialVideoSource;
    dark: TutorialVideoSource;
  };
  keyTakeaways: string[];
}

// Cloudinary URL builder helper with f_auto,q_auto
export const buildCloudinaryVideoUrl = (publicId: string, cloudName = 'unihousingmw'): string => {
  return `https://res.cloudinary.com/${cloudName}/video/upload/f_auto,q_auto/${publicId}.mp4`;
};

export const buildCloudinaryPosterUrl = (publicId: string, cloudName = 'unihousingmw'): string => {
  return `https://res.cloudinary.com/${cloudName}/video/upload/so_0,f_auto,q_auto/${publicId}.jpg`;
};

export const TUTORIAL_MODULES: TutorialModule[] = [
  // 1. Registration & Onboarding
  {
    id: 'registration-onboarding',
    title: 'Account Registration & Identity Verification',
    category: 'client',
    categoryLabel: 'Getting Started',
    audienceBadge: 'All Users',
    badgeColor: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    description: 'Learn how to create an account, select your role (Client, Transporter, or Fleet Operator), and pass biometric KYC verification.',
    duration: '2:15',
    iconName: 'UserCheck',
    videos: {
      light: {
        url: 'https://res.cloudinary.com/unihousingmw/video/upload/v1790322287/registration-LIGHT_fnoval.webm',
        poster: 'https://res.cloudinary.com/unihousingmw/image/upload/v1790323482/Screenshot_From_2026-09-25_10-03-10_ycuq1m.png',
        publicId: 'tutorials/registration_light'
      },
      dark: {
        url: '', // Paste Cloudinary URL for Dark mode here
        poster: '',
        publicId: 'tutorials/registration_dark'
      }
    },
    steps: [
      {
        title: 'Choose Your Account Role',
        description: 'Navigate to /register and select whether you are hiring transport (Client) or providing logistics (Transporter / Fleet).',
        timestamp: '0:05'
      },
      {
        title: 'Provide Contact Details & Password',
        description: 'Enter your legal name, phone number (Malawi +265 or international), email, and secure password.',
        timestamp: '0:35'
      },
      {
        title: 'Biometric & Identity Verification',
        description: 'Complete the instant selfie scan and national ID / passport upload to activate your verified badge.',
        timestamp: '1:10'
      },
      {
        title: 'Explore Your Dashboard',
        description: 'Get oriented with your personalized dashboard, wallet setup, and notification preferences.',
        timestamp: '1:50'
      }
    ],
    keyTakeaways: [
      'Verification takes less than 24 hours (instant for AI-scanned documents)',
      'A verified badge increases job acceptance rates by over 400%',
      'Keep your phone handy for SMS OTP confirmation'
    ]
  },

  // 2. Creating a Job (Client)
  {
    id: 'creating-a-job',
    title: 'Posting & Financing a Cargo Job',
    category: 'client',
    categoryLabel: 'Client / Cargo Owner',
    audienceBadge: 'Clients',
    badgeColor: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    description: 'Step-by-step walkthrough of detailing your shipment, selecting cargo parameters, setting a budget, and funding secure escrow.',
    duration: '2:45',
    iconName: 'PackagePlus',
    videos: {
      light: {
        url: 'https://res.cloudinary.com/unihousingmw/video/upload/v1790322357/posting_job-LIGHT_me9uzl.webm',
        poster: '',
        publicId: 'tutorials/create_job_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/create_job_dark'
      }
    },
    steps: [
      {
        title: 'Open Job Creation Modal',
        description: 'Click "Post New Job" from your client dashboard or navigation bar.',
        timestamp: '0:08'
      },
      {
        title: 'Define Pickup & Destination',
        description: 'Select origin and drop-off locations using the interactive map or address search.',
        timestamp: '0:40'
      },
      {
        title: 'Specify Cargo Type & Weight',
        description: 'Choose cargo category (agricultural, construction, FMCG, fragile) and specify exact weight/volume.',
        timestamp: '1:15'
      },
      {
        title: 'Suggested AI Pricing & Escrow Deposit',
        description: 'Review our recommended market rate or set your custom price. Fund via PayChangu Mobile Money or Card into secure escrow.',
        timestamp: '1:55'
      },
      {
        title: 'Publish & Receive Driver Applications',
        description: 'Your job is instantly broadcasted to verified transporters within range.',
        timestamp: '2:30'
      }
    ],
    keyTakeaways: [
      'Funds remain protected in escrow until you approve final delivery proof',
      'Accurate cargo dimensions prevent transporter cancellation upon arrival',
      'You can schedule pickups in advance or request immediate dispatch'
    ]
  },

  // 3. Adding a Vehicle (Transporters & Fleet Managers)
  {
    id: 'adding-a-vehicle',
    title: 'Registering Vehicles (Single Transporter & Fleet)',
    category: 'transporter',
    categoryLabel: 'Transporter & Fleet',
    audienceBadge: 'Transporter & Fleet',
    badgeColor: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    description: 'How to register a transport vehicle with registration plates, capacity, COF documents, and insurance.',
    duration: '3:00',
    iconName: 'Truck',
    videos: {
      light: {
        url: 'https://res.cloudinary.com/unihousingmw/video/upload/v1790326819/Adding_a_vehicle-LIGHT_nurnr1.webm ',
        poster: '',
        publicId: 'tutorials/add_vehicle_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/add_vehicle_dark'
      }
    },
    steps: [
      {
        title: 'Navigate to "My Vehicles"',
        description: 'Go to your dashboard and select Vehicles > "Add New Vehicle".',
        timestamp: '0:10'
      },
      {
        title: 'Single Transporter Vehicle Upload',
        description: 'Solo drivers enter vehicle make, model, tonnage, number plate, and upload vehicle photos.',
        timestamp: '0:45'
      },
      {
        title: 'Fleet Manager Bulk Registration',
        description: 'Fleet managers can register multiple trucks at once and assign preliminary driver pairings.',
        timestamp: '1:30'
      },
      {
        title: 'Uploading COF & Insurance Papers',
        description: 'Submit Certificate of Fitness (COF) and transit insurance documents for automated compliance review.',
        timestamp: '2:10'
      },
      {
        title: 'Approval & Readiness',
        description: 'Once verified, your vehicle badge turns green and becomes eligible for bidding on cargo.',
        timestamp: '2:45'
      }
    ],
    keyTakeaways: [
      'Clear photos of the license plate and cargo bed speed up verification',
      'Tonnage capacity determines which high-yield loads you qualify for',
      'Fleet managers can manage maintenance logs per vehicle'
    ]
  },

  // 4. Booking a Job (Transporter & Fleet Manager)
  {
    id: 'booking-a-job',
    title: 'Finding, Bidding & Securing Loads',
    category: 'transporter',
    categoryLabel: 'Transporter & Fleet',
    audienceBadge: 'Transporters & Fleet',
    badgeColor: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    description: 'Explore verified loads on the marketplace, check route profitability, submit bids, or execute instant bookings.',
    duration: '2:30',
    iconName: 'Briefcase',
    videos: {
      light: {
        url: '',
        poster: '',
        publicId: 'tutorials/booking_job_light'
      },
      dark: {
        url: 'https://res.cloudinary.com/unihousingmw/video/upload/v1790322342/securing_a_job-LIGHT_yey54c.webm',
        poster: '',
        publicId: 'tutorials/booking_job_dark'
      }
    },
    steps: [
      {
        title: 'Browse the Live Load Board',
        description: 'Filter by route (e.g. Blantyre to Lilongwe), cargo weight, and payout rate.',
        timestamp: '0:12'
      },
      {
        title: 'Transporter Solo Booking',
        description: 'Solo operators click "Book Now" for instant acceptance or submit a custom quote.',
        timestamp: '0:50'
      },
      {
        title: 'Fleet Manager Multi-Load Booking',
        description: 'Fleet managers reserve loads tailored to available drivers in their organization.',
        timestamp: '1:25'
      },
      {
        title: 'Escrow Confirmation & Job Locks',
        description: 'Once booked, the escrow funds are locked for your vehicle and contact details are exchanged.',
        timestamp: '2:00'
      }
    ],
    keyTakeaways: [
      'Instant-book jobs require no negotiation and guarantee the posted price',
      'All payments are guaranteed in escrow before your truck leaves the depot',
      'Top-rated drivers get early access to high-priority corporate contracts'
    ]
  },

  // 5. Driver Registration (Fleet Driver via Invite)
  {
    id: 'driver-registration',
    title: 'Fleet Driver Registration & Onboarding',
    category: 'fleet-driver',
    categoryLabel: 'Fleet Operations',
    audienceBadge: 'Fleet Drivers',
    badgeColor: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
    description: 'How a hired driver signs up using a Fleet Manager invite code and links their smartphone for live dispatch.',
    duration: '1:50',
    iconName: 'IdCard',
    videos: {
      light: {
        url: '',
        poster: '',
        publicId: 'tutorials/driver_registration_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/driver_registration_dark'
      }
    },
    steps: [
      {
        title: 'Receive Fleet Invite Link or Code',
        description: 'Open the invite SMS or link sent by your fleet operator (e.g. /register/driver?code=KC-FLT-xxx).',
        timestamp: '0:05'
      },
      {
        title: 'Complete Driver Profile & License Upload',
        description: 'Enter your driver details and upload your professional driving permit / class license.',
        timestamp: '0:35'
      },
      {
        title: 'Automatic Fleet Association',
        description: "Your profile instantly links to your fleet manager's dispatch roster.",
        timestamp: '1:10'
      },
      {
        title: 'Permission Check (GPS & Background Mode)',
        description: 'Grant location permissions to enable mandatory automated tracking during active deliveries.',
        timestamp: '1:35'
      }
    ],
    keyTakeaways: [
      'No registration fee for fleet drivers',
      'Driver earnings and trip settlements are tracked transparently',
      'Background GPS permission is only active when you start an assigned job'
    ]
  },

  // 6. Dispatching Loads (Fleet Managers to Drivers)
  {
    id: 'dispatching-loads',
    title: 'Fleet Dispatch: Assigning Jobs to Drivers',
    category: 'fleet-manager',
    categoryLabel: 'Fleet Operations',
    audienceBadge: 'Fleet Managers',
    badgeColor: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    description: 'Learn how fleet managers allocate booked jobs, select optimal drivers based on location, and monitor active assignments.',
    duration: '2:20',
    iconName: 'Send',
    videos: {
      light: {
        url: '',
        poster: '',
        publicId: 'tutorials/dispatch_load_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/dispatch_load_dark'
      }
    },
    steps: [
      {
        title: 'Access Fleet Dispatch Console',
        description: 'Navigate to Dashboard > Fleet Dispatch to see all unassigned confirmed bookings.',
        timestamp: '0:08'
      },
      {
        title: 'Select Job & Review Driver Proximity',
        description: 'View real-time locations and duty status of all drivers in your roster.',
        timestamp: '0:45'
      },
      {
        title: 'Assign Truck & Driver Pair',
        description: 'Select the target vehicle and driver, set commission splits, and click "Dispatch Load".',
        timestamp: '1:20'
      },
      {
        title: 'Driver Notification & Acknowledgment',
        description: 'The driver receives a high-priority push notification and trip manifest on their device.',
        timestamp: '1:55'
      }
    ],
    keyTakeaways: [
      'Dispatched jobs can be reassigned if a driver encounters mechanical issues',
      'Automated dispatch alerts notify the client that their truck is en route to pickup',
      'Fleet managers retain full visibility of telemetry and cargo status'
    ]
  },

  // 7. Tracking Transporters (Live GPS & Telemetry)
  {
    id: 'tracking-transporters',
    title: 'Real-Time GPS Tracking & Milestone Alerts',
    category: 'client',
    categoryLabel: 'Tracking & Visibility',
    audienceBadge: 'Clients & Fleet',
    badgeColor: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
    description: 'Monitor cargo in transit with live map telemetry, speed monitoring, ETA recalculation, and geofence alerts.',
    duration: '2:10',
    iconName: 'Navigation',
    videos: {
      light: {
        url: 'https://res.cloudinary.com/unihousingmw/video/upload/v1790322315/tracking_and_delivery-LIGHT_me9c6b.webm',
        poster: '',
        publicId: 'tutorials/tracking_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/tracking_dark'
      }
    },
    steps: [
      {
        title: 'Open the Live Tracking Console',
        description: 'Click "Track Shipment" from any active job card or SMS tracking link.',
        timestamp: '0:05'
      },
      {
        title: 'Live Moving Map & Route Breadcrumbs',
        description: 'View the transporter vehicle moving in real-time along the prescribed highway corridor.',
        timestamp: '0:38'
      },
      {
        title: 'Dynamic ETA & Status Indicators',
        description: 'System automatically updates expected arrival time based on road conditions and speed.',
        timestamp: '1:15'
      },
      {
        title: 'Share Tracking Link',
        description: 'Generate an external tracking link to send to warehouse receivers or consignees.',
        timestamp: '1:45'
      }
    ],
    keyTakeaways: [
      'Tracking updates every 10 seconds via WebSockets with low data consumption',
      'Consignees can track shipments without creating an account',
      'Automatic alerts are sent when the driver is within 5km of drop-off'
    ]
  },

  // 8. Steps After Securing a Job (Pickup, In Transit, Delivery)
  {
    id: 'job-lifecycle-stages',
    title: 'Job Lifecycle: Pick Up, In Transit & Delivered',
    category: 'transporter',
    categoryLabel: 'Execution Workflow',
    audienceBadge: 'Transporters & Drivers',
    badgeColor: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
    description: 'The complete on-the-road process: arriving at origin, taking cargo pickup photos, driving with telemetry, and digital delivery sign-off.',
    duration: '3:15',
    iconName: 'CheckCircle',
    videos: {
      light: {
        url: '',
        poster: '',
        publicId: 'tutorials/job_lifecycle_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/job_lifecycle_dark'
      }
    },
    steps: [
      {
        title: 'Stage 1: Arrival & Proof of Pickup',
        description: 'Arrive at pickup location, tap "Arrived at Pickup", take cargo photos, and confirm initial inspection.',
        timestamp: '0:15'
      },
      {
        title: 'Stage 2: Start Trip (In Transit)',
        description: 'Tap "Departed with Cargo". GPS tracking activates automatically. Follow recommended turn-by-turn navigation.',
        timestamp: '1:05'
      },
      {
        title: 'Stage 3: Arrival at Destination',
        description: 'Arrive at drop-off point, inspect cargo condition with the consignee, and tap "Arrived for Delivery".',
        timestamp: '1:50'
      },
      {
        title: 'Stage 4: Digital Signature & Proof of Delivery (POD)',
        description: 'Capture photo of unloaded cargo. Have receiver sign on your phone screen with their finger.',
        timestamp: '2:30'
      },
      {
        title: 'Stage 5: Instant Escrow Release',
        description: 'Once POD is submitted, client approves or system auto-releases payment directly to your wallet.',
        timestamp: '2:55'
      }
    ],
    keyTakeaways: [
      'Always capture clear pickup photos to protect against pre-existing cargo damage claims',
      'Digital signature with timestamp serves as legal proof of delivery (POD)',
      'Escrow funds release automatically within 2 hours of verified POD'
    ]
  },

  // 9. Emergency Actions & SOS
  {
    id: 'emergency-actions-sos',
    title: 'Emergency Actions, Breakdowns & SOS Protocol',
    category: 'emergency',
    categoryLabel: 'Safety & Emergency',
    audienceBadge: 'Critical Safety',
    badgeColor: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
    description: 'What to do in case of mechanical breakdown, road accident, cargo tampering, or security distress during transit.',
    duration: '2:00',
    iconName: 'AlertTriangle',
    videos: {
      light: {
        url: '',
        poster: '',
        publicId: 'tutorials/emergency_sos_light'
      },
      dark: {
        url: '',
        poster: '',
        publicId: 'tutorials/emergency_sos_dark'
      }
    },
    steps: [
      {
        title: 'Triggering the Emergency SOS Button',
        description: 'Tap the red floating SOS button in the mobile or web app to broadcast high-priority distress alerts.',
        timestamp: '0:10'
      },
      {
        title: 'Selecting Incident Type',
        description: 'Quickly select: Breakdown / Mechanical, Road Accident, Security Threat, or Cargo Damage.',
        timestamp: '0:35'
      },
      {
        title: 'Incident Dispatch & Roadside Assistance',
        description: 'System automatically notifies local recovery partners, police/paramedics if necessary, and fleet management.',
        timestamp: '1:05'
      },
      {
        title: 'Secondary Vehicle Transshipment',
        description: 'If your vehicle is immobilized, our dispatch team arranges emergency transshipment to transfer cargo safely.',
        timestamp: '1:40'
      }
    ],
    keyTakeaways: [
      'SOS button transmits your precise GPS coordinates immediately even with low network connectivity',
      'Emergency roadside recovery partners are pre-vetted across key Malawian transport corridors',
      'Never hesitate to trigger SOS in cases of personal safety or vehicle compromise'
    ]
  }
];
